import { Benefits } from '@sunwebgroup/uds-ui/benefits'
import { Button } from '@sunwebgroup/uds-ui/button'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { useGolfServiceStore } from '@/scopes/checkout/components/services/GolfService/store'
import { removeGolfSlot } from '@/scopes/checkout/domain/actions/services/golf'
import { useService } from '@/scopes/checkout/domain/hooks'
import { GOLF_NEW_SLOT_KEY, ServiceType } from '@/scopes/checkout/domain/types'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { formatGolfDate, getGolfSlotSummary } from '../helpers'
import { golfSlotPriceSelector } from '../selectors'
import styles from './GolfServiceSlotCard.module.css'

interface GolfServiceSlotCardProps {
  slotId: string
}

export function GolfServiceSlotCard({ slotId }: GolfServiceSlotCardProps) {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.Golf)
  const { isTablet } = useBreakpoints()

  const { startEditingSlot } = useGolfServiceStore()

  const summary = getGolfSlotSummary(service, slotId)
  const slotPrice = golfSlotPriceSelector(service, slotId)
  const isNewSlot = slotId === GOLF_NEW_SLOT_KEY

  const title = summary
    ? [
        summary.locationName,
        formatGolfDate(summary.date),
        `${summary.time || intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_time_unspecified_label' })}`,
      ].filter(Boolean).join(' · ')
    : ''

  function handleRemoveClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (!service)
      return
    setServiceValue(removeGolfSlot({ slotId })(service))
  }

  function handleEditClick(e: React.MouseEvent) {
    e.stopPropagation()
    startEditingSlot(slotId)
  }

  if (!service)
    return null

  if (isNewSlot) {
    return (
      <div className={styles.card}>
        <div className={styles.newSlot}>
          <Button variant="secondary" size="S" onClick={handleEditClick}>
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_add_slot_label' })}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <TitleListCell size="L" title={title} />
          <Benefits.Root className={styles.overline} display="list">
            {summary?.holes && (
              <Benefits.Item
                icon="GolfClub"
                title={intl.formatMessage(
                  { id: 'checkout_steps_ancillaries_golf_holes_text_description' },
                  { holes: summary.holes },
                )}
              />
            )}
            <Benefits.Item
              icon="GolfCart"
              title={
                summary?.withBuggy
                  ? intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_with_buggy_text' })
                  : intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_without_buggy_text' })
              }
            />
            {summary && (
              <Benefits.Item
                icon="Person"
                title={summary.participantIds.map(pid => service.participants[pid]?.name ?? pid).join(', ')}
              />
            )}
          </Benefits.Root>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>
          {slotPrice && (
            <div className={styles.priceBlock}>
              <Typography variant="body-m-bold-snug" className={styles.price}>
                {intl.formatCurrency(slotPrice.price, { showSign: slotPrice.price !== 0 })}
              </Typography>
            </div>
          )}
          <div className={styles.buttonActions}>
            <Button
              variant="secondary"
              size="S"
              onClick={handleRemoveClick}
              aria-label={intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_remove_button' })}
            >
              {isTablet && <Button.Icon size={16} glyph="Dismiss" />}
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_remove_button' })}
            </Button>
            <Button variant="secondary" size="S" onClick={handleEditClick}>
              {isTablet && <Button.Icon size={16} glyph="Pencil" />}
              {summary
                ? intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_edit_button' })
                : intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_add_button' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
